import { userPermissionAdminApi } from '@/api/admin/user-permission';
import type { RootState } from '@/store';
import type { IThunkAPI } from '@/store/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  IUserPermissionState,
  ILxStaffSummary,
  IPermissionWithLevel,
  IUserPermissionRequest,
  IPermissionGroup,
  IPermissionAndGroupPost,
  IUserPermissionGroupRequest,
  IUserPermissionPermissionRequest,
  IUsersWithImpersonationPermission,
} from '../types/types';
import { adminImpersonateApi } from '@/api/admin/admin-impersonate';

const initialState: IUserPermissionState = {
  userByTypesList: [],
  userPermissionList: [],
  userPermissions: [],
  userPermissionGroupList: [],
};

export const getUserListByTypes = createAsyncThunk<Array<ILxStaffSummary>, string, IThunkAPI>(
  'get/user/byTypes/list',
  async (typeIds, thunkAPI) => {
    try {
      const response = await userPermissionAdminApi.getUserListByTypes(typeIds);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getUserPermissionList = createAsyncThunk<Array<IPermissionWithLevel>, IUserPermissionRequest, IThunkAPI>(
  'get/gig/permission/client/clientId/user/userId/list',
  async (requestPayload, thunkAPI) => {
    try {
      const response = await userPermissionAdminApi.getUserPermission(requestPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getUserPermissionGroupList = createAsyncThunk<Array<IPermissionGroup>, IUserPermissionRequest, IThunkAPI>(
  'get/gig/permission/group/client/clientId/user/userId/list',
  async (requestPayload, thunkAPI) => {
    try {
      const response = await userPermissionAdminApi.getUserPermissionGroup(requestPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getUserPermissionGroupByClientIdAndGroupIdList = createAsyncThunk<
  Array<ILxStaffSummary>,
  IUserPermissionGroupRequest,
  IThunkAPI
>('get/user/permission/client/clientId/group/groupId/list', async (requestPayload, thunkAPI) => {
  try {
    const response = await userPermissionAdminApi.getUserPermissionGroupByClientAndGroupId(requestPayload);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getUserPermissionByClientIdAndPermissionIdList = createAsyncThunk<
  Array<ILxStaffSummary>,
  IUserPermissionPermissionRequest,
  IThunkAPI
>('get/user/permission/user/client/clientId/permission/permissionId/list', async (requestPayload, thunkAPI) => {
  try {
    const response = await userPermissionAdminApi.getUserPermissionByClientAndPermissionId(requestPayload);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const saveUserPermissionsWithGroups = createAsyncThunk<unknown, IPermissionAndGroupPost, IThunkAPI>(
  'post/gig/permission/client/clientId/user/userId/group/set',
  async (permissions, thunkAPI) => {
    try {
      const response = await userPermissionAdminApi.savePermissionWithGroup(permissions);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getUsersWithImpersonatePermission = createAsyncThunk<IUsersWithImpersonationPermission[], void, IThunkAPI>(
  'admin/impersonation/getUsers',
  async (_, thunkAPI) => {
    try {
      const response = await adminImpersonateApi.getUsersWithImpersonatePermission();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const impersonateUser = createAsyncThunk<any, number, IThunkAPI>(
  'admin/impersonateUser',
  async (userId, thunkAPI) => {
    try {
      const response = await adminImpersonateApi.impersonateUser(userId);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const userPermissionAdminSlice = createSlice({
  name: 'userPermissionAdmin',
  initialState,
  reducers: {
    clearUserByTypesList(state) {
      state.userByTypesList = [];
    },
    clearUserPermissionList(state) {
      state.userPermissionList = [];
    },
    clearUserPermissionGroupList(state) {
      state.userPermissionGroupList = [];
    },
    clearUserPermissionGroupByClientAndGroupIdList(state) {
      state.userPermissions = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getUserListByTypes.fulfilled, (state, action) => {
        state.userByTypesList = action.payload;
      })
      .addCase(getUserPermissionList.fulfilled, (state, action) => {
        state.userPermissionList = action.payload;
      })
      .addCase(getUserPermissionGroupList.fulfilled, (state, action) => {
        state.userPermissionGroupList = action.payload;
      })
      .addCase(getUserPermissionGroupByClientIdAndGroupIdList.fulfilled, (state, action) => {
        state.userPermissions = action.payload;
      })
      .addCase(getUserPermissionByClientIdAndPermissionIdList.fulfilled, (state, action) => {
        state.userPermissions = action.payload;
      });
  },
});

// actions
export const {
  clearUserByTypesList,
  clearUserPermissionList,
  clearUserPermissionGroupList,
  clearUserPermissionGroupByClientAndGroupIdList,
} = userPermissionAdminSlice.actions;

// selectors
export const userByTypesList = (state: RootState): IUserPermissionState['userByTypesList'] =>
  state.userPermissionAdmin.userByTypesList;
export const userPermissionList = (state: RootState): IUserPermissionState['userPermissionList'] =>
  state.userPermissionAdmin.userPermissionList;
export const userPermissionGroupList = (state: RootState): IUserPermissionState['userPermissionGroupList'] =>
  state.userPermissionAdmin.userPermissionGroupList;
export const userPermissions = (state: RootState): IUserPermissionState['userPermissions'] =>
  state.userPermissionAdmin.userPermissions;

export default userPermissionAdminSlice.reducer;
